import React from 'react';
import { useTheme } from '../theme';
import { LogoutIcon, MoonIcon, SunIcon } from './icons';
import logo from '../assets/haz_h.svg';

export interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  title?: string;
}

interface AppShellProps {
  title: string;
  nav: NavItem[];
  activeNav: string;
  onNavigate: (key: string) => void;
  headerActions?: React.ReactNode;
  userName: string;
  onLogout: () => void;
  children: React.ReactNode;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

// The frame every Hazcheck product shares: an 85px icon rail on the left and a
// 51px header, both sized by the theme's layout tokens.
const AppShell: React.FC<AppShellProps> = ({
  title,
  nav,
  activeNav,
  onNavigate,
  headerActions,
  userName,
  onLogout,
  children,
}) => {
  const [theme, toggleTheme] = useTheme();

  return (
    <div className="shell">
      <aside className="shell-sidebar">
        <div className="shell-logo">
          <img src={logo} alt="Hazcheck" />
        </div>

        <nav className="shell-nav" aria-label="Sections">
          {nav.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`shell-nav-item ${activeNav === item.key ? 'is-active' : ''}`}
              onClick={() => onNavigate(item.key)}
              disabled={item.disabled}
              title={item.title}
              aria-current={activeNav === item.key ? 'page' : undefined}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="shell-sidebar-footer">
          <button
            type="button"
            className="shell-nav-item"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          <button type="button" className="shell-nav-item" onClick={onLogout} title="Sign out">
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <div className="shell-body">
        <header className="shell-header">
          <h1 className="h4">{title}</h1>
          <div className="shell-header-actions">
            {headerActions}
            <span className="user-chip" title={userName}>
              <span className="user-avatar" aria-hidden="true">{initials(userName) || '?'}</span>
              <span className="user-name">{userName}</span>
            </span>
          </div>
        </header>

        <main className="shell-main">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
